/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module   public/js/services/contactMetaService.js
 * @class    ContactMetaService
 * @layer    service
 * @summary  Fetches a Contact’s core meta once, normalises it, and
 *           caches the result for the duration of the page-session.
 *
 * @description
 * Workflow
 *  1. Uses `endpoints.getContactDetails({ userId, contactId })`
 *     to hit the CRM proxy (HubSpot/SF).
 *  2. Extracts and normalises:
 *        • **name**   – “First Last”, or fallback to `fullname|name`.
 *        • **title**  – job title (mixed-case).
 *        • **linkedinUrl** – direct LinkedIn profile URL if present.
 *        • **topsail_analysis_company_report_focusmode** – multi-line text
 *          stored on the Contact object; rendered in Focus-Mode card.
 *  3. Looks for tenant-scoped overrides in `/ContactMeta/{contactId}` and
 *     merges them over the CRM fields.
 *  4. Caches the result in-memory (`#cache`) keyed by `contactId` to avoid
 *     duplicate network calls.
 *
 * ## Public API

 * • `constructor({ apiClient, endpoints })`

 * • `get(contactId, userId) → Promise<{ name, title, companyName, linkedinUrl, topsail_analysis_company_report_focusmode }>`

 * • `update(contactId, fields) → Promise<void>` – persist overrides into
 *   `/ContactMeta/{contactId}` and update the in-memory cache.
 *
 * ## Dependencies (DI)
 * • **apiClient**   – thin wrapper around `fetchJson()` with auth.  
 * • **endpoints**   – resolver that builds REST URLs for the CRM proxy.
 *
 * @author   TopSail AI
 * @version  1.1.0
 * @since    2025-05-13
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { lookupStagedLinkedinUrl } from './contactsStagingLinkedinFinder.js';
import { docWithTenant, writeWithTenant } from '../utils/firestoreHelpers.js';
import { normalizeContactTimeZone } from '../utils/contactTimeZone.js';
import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { resolveRuntimeDi } from '../utils/runtimeDi.js';

/**
 * Return the first property in `keys` whose value on `obj` is not `undefined`.
 *
 * Empty strings are treated as explicit overrides and returned as-is, whereas
 * properties that are completely absent or set to `undefined` are skipped.
 *
 * @param {Record<string, any>} obj
 * @param {string[]} keys
 * @returns {any}
 */
function pickFirstDefined(obj, keys) {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined) return value;
    }
  }
  return undefined;
}

export default class ContactMetaService {
  #apiClient;

  #endpoints;

  #auth;

  #crmUtils;

  /**
   * @type {Record<string, {
   *   name:string,
   *   title:string,
   *   companyName:string,
   *   linkedinUrl:string
   * }>}
   */
  #cache = {};

  /** @type {Set<string>} contactIds with no CRM record */
  #crmLess = new Set();

  /**
   * Create a contact metadata service with optional runtime dependency overrides.
   *
   * @param {{ apiClient: object, endpoints: object, auth?: object|null, crmUtils?: object|null }} deps - Service dependencies.
   */
  constructor({ apiClient, endpoints, auth = null, crmUtils = null }) {
    this.#apiClient = apiClient;
    this.#endpoints = endpoints;
    this.#auth = auth;
    this.#crmUtils = crmUtils;
  }

  /**
   * @param {string} contactId
   * @param {string} userId
   * @returns {Promise<{
   *   name: string,
   *   title: string,
   *   companyName: string,
   *   linkedinUrl: string,
   *   topsail_analysis_company_report_focusmode: string,
   *   crmLess: boolean,
   * }>} contact meta merged with tenant overrides
   */
  async get(contactId, userId) {
    // 1. Memory cache
    if (this.#cache[contactId]) return this.#cache[contactId];

    // ── Resolve tenant once
    let tenantId;
    const runtimeDi = resolveRuntimeDi();
    const auth = this.#auth || runtimeDi.auth;
    const crmUtils = this.#crmUtils || runtimeDi.crmUtils;
    try {
      const user = auth?.currentUser ?? (await auth?.waitForLogin?.());
      tenantId = user?.tenantId;
    } catch {
      /* noop */
    }

    // ── Build state holders
    let contact = {};
    let props = {};
    let crmLess = false;
    let crm = 'local';
    let contactsMetaData = null;
    let contactsMetaLoadError = null;

    try {
      if (userId) crm = await crmUtils?.getCurrentCRM?.(userId);
    } catch {
      crm = 'local';
    }
    if (crm === 'local') crmLess = true;

    // A) Record ContactsMeta presence separately from crmLess state
    if (tenantId) {
      try {
        const ref = docWithTenant(tenantId, `ContactsMeta/${contactId}`);
        const snap = await getDoc(ref);
        if (snap?.exists?.()) {
          contactsMetaData =
            typeof snap.data === 'function' ? (snap.data() ?? {}) : {};
        }
      } catch (err) {
        contactsMetaLoadError = err;
      }
    }

    // B) Attempt CRM fetch whenever the tenant CRM is remote
    if (!crmLess) {
      const crmContactId =
        crm === 'hubspot' &&
        /^\d+$/.test(String(contactsMetaData?.crmContactId || '').trim())
          ? String(contactsMetaData.crmContactId).trim()
          : contactId;
      const url = this.#endpoints.getContactDetails({
        userId,
        contactId: crmContactId,
        crm,
      });
      try {
        const resp = await this.#apiClient.fetchJson(url, { auth: true });
        contact = resp.contact || {};
        props = contact.properties || {};
      } catch (err) {
        if (window?.__FM_DEBUG === true) {
          console.debug(
            '[ContactMeta] CRM fetch failed; falling back to local merges',
            {
              contactId,
              crm,
              message: err?.message || String(err),
            }
          );
        }
        // Proxy failed → treat as CRM-less and continue with local merges
        crmLess = true;
      }
    }
    const first = props.firstname || '';
    const last = props.lastname || '';

    /** @type {Record<string, any>} */
    const meta = {
      name:
        [first, last].filter(Boolean).join(' ').trim() ||
        props.fullname ||
        props.name ||
        '',
      title: props.jobtitle || props.jobTitle || '',
      companyName:
        props.companyName ||
        (contact.associations?.companies?.results?.[0]?.properties?.name ?? ''),
      linkedinUrl: props.hs_linkedin_url || '',
      topsail_analysis_company_report_focusmode:
        props.topsail_analysis_company_report_focusmode ||
        props.ai_analysis_company_report_focusmode ||
        '',
      firstName: first,
      lastName: last,
      jobTitle: props.jobtitle || props.jobTitle || '',
      company:
        props.companyName ||
        (contact.associations?.companies?.results?.[0]?.properties?.name ?? ''),
      domain: '',
      companyWebsite: '',
      companyIndustry: '',
      companyEmployees: '',
      companyEmployeeCount: '',
      companyCity: '',
      companyState: '',
      companyCountry: '',
      email: props.email || '',
      phone:
        props.phone ||
        props.phoneNumber ||
        props.mobilephone ||
        props.mobilePhone ||
        '',
      mobilePhone: props.mobilephone || props.mobilePhone || '',
      officePhone: props.phone || props.phoneNumber || '',
      mainOfficePhone: '',
      contactTimeZone: null,
    };

    // C) Merge tenant-scoped data when available
    if (tenantId && contactsMetaData) {
      const data = contactsMetaData;
      const overrides = {};

      if (
        Object.prototype.hasOwnProperty.call(data, 'firstName') ||
        Object.prototype.hasOwnProperty.call(data, 'lastName')
      ) {
        overrides.name =
          `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim();
      }

      if (Object.prototype.hasOwnProperty.call(data, 'jobTitle')) {
        overrides.title = data.jobTitle ?? '';
      }

      if (Object.prototype.hasOwnProperty.call(data, 'company')) {
        overrides.companyName = data.company ?? '';
      }

      if (Object.prototype.hasOwnProperty.call(data, 'email')) {
        overrides.email = data.email ?? '';
      }

      const phoneOverride = pickFirstDefined(data, [
        'phone',
        'mobilePhone',
        'officePhone',
        'mainOfficePhone',
      ]);
      if (phoneOverride !== undefined) {
        overrides.phone = phoneOverride;
      }

      Object.assign(meta, data, overrides);
    } else if (crmLess && contactsMetaLoadError) {
      console.warn(
        '[ContactMeta] failed to load ContactsMeta',
        contactsMetaLoadError
      );
    }

    if (crmLess && tenantId) {
      this.#crmLess.add(contactId);
    } else if (tenantId) {
      this.#crmLess.delete(contactId);
    }

    // D) Merge tenant overrides (always last)
    if (tenantId) {
      try {
        const ref = docWithTenant(tenantId, `ContactMeta/${contactId}`);
        const snap = await getDoc(ref);
        if (snap?.exists()) {
          const overrides = snap.data() || {};
          Object.assign(meta, overrides);
          if (Object.prototype.hasOwnProperty.call(overrides, 'company')) {
            meta.companyName = overrides.company ?? '';
          }
        }
      } catch (err) {
        console.warn('[ContactMeta] failed to load overrides', err);
      }
    }

    // Only synthesise phone when it is truly missing. Explicit empty-string
    // overrides must be preserved so cleared values don't resurrect.
    if (meta.phone === undefined) {
      meta.phone =
        meta.mobilePhone ?? meta.officePhone ?? meta.mainOfficePhone ?? '';
    }
    meta.contactTimeZone = normalizeContactTimeZone(meta.contactTimeZone);

    meta.crmLess = crmLess;

    if (!meta.linkedinUrl) {
      const fsUrl = await lookupStagedLinkedinUrl(contactId, {
        auth,
        crmUtils,
      });
      if (fsUrl) meta.linkedinUrl = fsUrl;
    }

    this.#cache[contactId] = meta;
    return meta;
  }

  /**
   * Persist edited contact fields into Firestore `/ContactMeta/{contactId}`.
   * Updates the local cache so subsequent reads reflect the changes without
   * another network call.
   *
   * @param {string} contactId
   * @param {Record<string, string>} fields
   * @param {{ tenantId?: string }} [options] - Optional explicit tenant scope.
   * @returns {Promise<void>}
   */
  async update(contactId, fields = {}, options = {}) {
    if (!contactId) return;
    const runtimeDi = resolveRuntimeDi();
    const auth = this.#auth || runtimeDi.auth;
    const user = auth?.currentUser ?? (await auth?.waitForLogin?.());
    const tenantId =
      typeof options?.tenantId === 'string' && options.tenantId.trim()
        ? options.tenantId.trim()
        : user?.tenantId;
    if (!tenantId) throw new Error('Missing tenant ID');

    const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
    const payload = { ...fields };
    if (hasOwn(payload, 'contactTimeZone')) {
      payload.contactTimeZone = normalizeContactTimeZone(
        payload.contactTimeZone
      );
    }
    const hasFirst = hasOwn(fields, 'firstName');
    const hasLast = hasOwn(fields, 'lastName');

    if (hasFirst || hasLast) {
      const cached = this.#cache[contactId] || {};
      const first = hasFirst
        ? (fields.firstName ?? '')
        : (cached.firstName ?? '');
      const last = hasLast ? (fields.lastName ?? '') : (cached.lastName ?? '');

      payload.name = `${first} ${last}`.trim();
    }

    const ref = docWithTenant(tenantId, `ContactMeta/${contactId}`);
    await writeWithTenant(tenantId, ref, payload, { merge: true });

    let shouldWriteContactsMeta = this.#crmLess.has(contactId);
    if (!shouldWriteContactsMeta) {
      try {
        const cmRef = docWithTenant(tenantId, `ContactsMeta/${contactId}`);
        const cmSnap = await getDoc(cmRef);
        shouldWriteContactsMeta = cmSnap?.exists() || false;
        if (shouldWriteContactsMeta) {
          await writeWithTenant(tenantId, cmRef, payload, { merge: true });
        }
      } catch (err) {
        console.warn('[ContactMeta] failed dual-write ContactsMeta', err);
      }
    } else {
      const cmRef = docWithTenant(tenantId, `ContactsMeta/${contactId}`);
      await writeWithTenant(tenantId, cmRef, payload, { merge: true });
    }

    if (this.#cache[contactId]) {
      this.#cache[contactId] = {
        ...this.#cache[contactId],
        ...payload,
      };
    }
  }
}
